import { Injectable } from '@angular/core';
import { MessageMapper } from '@cpd-app/core-module/mappers/message.mapper';
import { StreamRequest, StreamResponse } from '@cpd-app/core-module/models';
import {
  UtilityService,
  ViaNudgesService
} from '@cpd-app/core-module/services';
import { StreamService } from '@cpd-app/core-module/services/ai-chat/stream.service';
import {
  DeleteConversationGQL,
  GetConversationHistoryGQL,
  GetRateLimitGQL,
  GetUserConversationsGQL,
  RateMessageGQL
} from '@cpd-app/generated';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { DeviceFacade } from '../device';
import * as GoalActions from '../goal/goal.actions';

import * as AiChatActions from './ai-chat.actions';
import { AiChatFacade } from './ai-chat.facade';
import {
  applyViaPanelSize,
  isNarrowViewport,
  viaPanelSize
} from './via-panel-width';

@Injectable()
export class AiChatEffects {
  constructor(
    private actions$: Actions,
    private getConversationHistoryGQL: GetConversationHistoryGQL,
    private getUserConversationsGQL: GetUserConversationsGQL,
    private deleteConversationGQL: DeleteConversationGQL,
    private getRateLimitGQL: GetRateLimitGQL,
    private rateMessageGQL: RateMessageGQL,
    private aiChatfacade: AiChatFacade,
    private streamService: StreamService,
    private viaNudgesService: ViaNudgesService,
    private deviceFacade: DeviceFacade
  ) {}

  getConversationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.getConversationHistory),
      switchMap(({ conversationId, limit, offset }) =>
        this.getConversationHistoryGQL
          .fetch({
            conversationId,
            limit,
            offset
          })
          .pipe(
            map((result) => {
              const data = result?.data?.getConversationHistory;

              if (data) {
                return AiChatActions.getConversationHistorySuccess({
                  messages: (data.messages || [])
                    .sortByAsc((msg) => msg.id)
                    .map((msg) => MessageMapper.toChatMessage(msg))
                });
              }

              return AiChatActions.getConversationHistoryFailure({
                error: 'No conversation history found'
              });
            }),
            catchError((error) =>
              of(
                AiChatActions.getConversationHistoryFailure({
                  error: error.message
                })
              )
            )
          )
      )
    )
  );

  loadMoreConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.loadConversations),
      switchMap(({ offset, limit }) =>
        this.getUserConversationsGQL
          .fetch({
            limit,
            offset
          })
          .pipe(
            map((result) => {
              const data = result.data?.getUserConversations;
              if (data) {
                const conversationsCount = data.conversationCount;
                const conversations = data.conversations
                  .sortByDesc((c) => +c.updatedAt)
                  .map((c) => ({
                    ...c,
                    updatedAt: new Date(+c.updatedAt).toISOString()
                  }));

                return AiChatActions.loadConversationsSuccess({
                  conversations,
                  conversationsCount
                });
              }

              return AiChatActions.loadConversationsFailure({
                error: 'No more conversations found'
              });
            }),
            catchError((error) =>
              of(
                AiChatActions.loadConversationsFailure({
                  error: error.message
                })
              )
            )
          )
      )
    )
  );

  deleteConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.deleteConversation),
      switchMap(({ conversationId }) =>
        this.deleteConversationGQL
          .mutate({
            conversationId
          })
          .pipe(
            map((result) => {
              if (result.data?.deleteConversation) {
                return AiChatActions.deleteConversationSuccess({
                  conversationId
                });
              }

              return AiChatActions.deleteConversationFailure({
                error: 'Failed to delete conversation'
              });
            }),
            catchError((error) =>
              of(
                AiChatActions.deleteConversationFailure({
                  error: error.message
                })
              )
            )
          )
      )
    )
  );

  stream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.streamStart),
      withLatestFrom(
        this.aiChatfacade.currentConversationId$,
        this.aiChatfacade.messages$
      ),
      switchMap(([{ query, question }, conversationId, messages]) => {
        const request: StreamRequest = {
          query: query,
          conversationId: conversationId,
          // The question this message answers. Only sent in resume mode (see
          // StreamService), where Via's opener is written here and never saved.
          lastViaMessage:
            [...(messages ?? [])].reverse().find((m) => m.role === 'assistant')
              ?.content || undefined
        };

        if (question) {
          request.query = question.query;
          request.onetCodes = question.onetCodes;
          request.schoolIds = question.schoolIds;
          request.programIds = question.programIds;
          request.edlevelIds = question.edlevelIds;
          request.userId = question.userId;
          request.keyPoint = question.keyPoint;
          request.relatedTopic = question.relatedTopic;
        }

        // VIA-28 section 3.2. The page on EVERY turn, not just the first: the
        // question's own page, else the page the learner is on. The server
        // ignores it while VIA_PAGE_CONTEXT_ENABLED is off. No key at all when
        // there is no page, so an off-page request is unchanged.
        const page = question?.page ?? this.viaNudgesService.currentPage?.();
        if (page) {
          request.page = page;
        }

        return this.streamService.connect(request).pipe(
          filter((response: StreamResponse) => !!response),
          switchMap((response) => {
            const type = response.type;
            const data = response.data;

            if (type === 'connected') {
              return [AiChatActions.streamConnected()];
            }

            // A mid-stream failure must end the turn as a FAILURE. Falling
            // through to streamComplete below would leave isStreaming true only
            // until an empty completion landed, showing the learner a blank
            // answer instead of an error.
            //
            // V-1: the last sentence of this comment used to claim streamFailure
            // "surfaces the message, which is what re-enables the send button".
            // It did neither. The reducer did not clear isLoading, no template
            // read `error`, and the send control was re-enabled only inside a
            // streamComplete subscription a failure never satisfies. All three
            // are fixed now; the comment is corrected rather than deleted
            // because a comment that asserts working behaviour is exactly what
            // stops anyone checking.
            if (type === 'error' || data?.error) {
              return [
                AiChatActions.streamFailure({
                  error: data?.error || 'Failed to generate response'
                })
              ];
            }

            if (type === 'chunk' && data) {
              return [
                AiChatActions.streamChunk({
                  fullResponse: data.fullResponse
                })
              ];
            }

            let actions = [
              AiChatActions.streamComplete({
                response: data.response,
                messageId: data.messageId,
                goals: data.goals,
                successSaveGoal: data.successSaveGoal,
                courses: data.courses,
                certificates: data.certificates
              }),
              AiChatActions.loadConversations({ offset: 0, limit: 10 }),
              AiChatActions.getRateLimit()
            ];

            if (type === 'complete' && data) {
              /* refresh goals */
              if (data.successSaveGoal) {
                actions = [
                  ...actions,
                  GoalActions.loadGoals(),
                  GoalActions.loadGoalsReportDetails()
                ] as any[];
              }

              /* update conversation */
              if (conversationId) {
                return actions;
              }
              /* new conversation */
              return [
                ...actions,
                AiChatActions.setCurrentConversation({
                  conversationId: data?.conversationId
                })
              ];
            }

            return null;
          }),
          catchError((error) =>
            of(AiChatActions.streamFailure({ error: error.message }))
          )
        );
      })
    )
  );

  getRateLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.getRateLimit),
      switchMap(() =>
        this.getRateLimitGQL.fetch().pipe(
          switchMap((result) => {
            const data = result?.data?.getRateLimit;

            if (data) {
              const timeUntilReset = UtilityService.getTimeUntilResetString(
                data.resetTime
              );

              return [
                AiChatActions.getRateLimitSuccess({
                  allowed: data.allowed,
                  remaining: data.remaining,
                  limit: data.limit,
                  resetTime: new Date(data.resetTime).toISOString(),
                  timeUntilReset
                }),
                AiChatActions.startRateLimitCountdown({
                  resetTime: data.resetTime
                })
              ];
            }

            return [
              AiChatActions.getRateLimitFailure({
                error: 'No rate limit data found'
              })
            ];
          }),
          catchError((error) =>
            of(
              AiChatActions.getRateLimitFailure({
                error: error.message
              })
            )
          )
        )
      )
    )
  );

  countDownRateLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.startRateLimitCountdown),
      switchMap(({ resetTime }) => {
        return timer(0, 60000).pipe(
          map(() => {
            const timeUntilReset =
              UtilityService.getTimeUntilResetString(resetTime);

            return AiChatActions.updateTimeUntilReset({
              timeUntilReset
            });
          }),
          takeUntil(this.actions$.pipe(ofType(AiChatActions.closeChatVia)))
        );
      })
    )
  );

  likeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.likeMessage),
      switchMap(({ messageId, liked }) =>
        this.rateMessageGQL
          .mutate({
            messageId,
            liked: liked ? true : null,
            disliked: null
          })
          .pipe(
            map((result) => {
              if (result.data?.rateMessage) {
                const { id, liked, disliked } = result.data.rateMessage;
                return AiChatActions.rateMessageSuccess({
                  messageId: id,
                  liked: liked ?? null,
                  disliked: disliked ?? null
                });
              }

              return AiChatActions.rateMessageFailure({
                error: 'Failed to like message'
              });
            }),
            catchError((error) =>
              of(
                AiChatActions.rateMessageFailure({
                  error: error.message
                })
              )
            )
          )
      )
    )
  );

  dislikeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.dislikeMessage),
      switchMap(({ messageId, disliked }) =>
        this.rateMessageGQL
          .mutate({
            messageId,
            liked: null,
            disliked: disliked ? true : null
          })
          .pipe(
            map((result) => {
              if (result.data?.rateMessage) {
                const { id, liked, disliked } = result.data.rateMessage;
                return AiChatActions.rateMessageSuccess({
                  messageId: id,
                  liked: liked ?? null,
                  disliked: disliked ?? null
                });
              }

              return AiChatActions.rateMessageFailure({
                error: 'Failed to dislike message'
              });
            }),
            catchError((error) =>
              of(
                AiChatActions.rateMessageFailure({
                  error: error.message
                })
              )
            )
          )
      )
    )
  );

  openChatVia$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.openChatVia, AiChatActions.closeChatVia),
        withLatestFrom(
          this.aiChatfacade.isOpenChatVia$,
          this.deviceFacade.isMobile$
        ),
        tap(([, isOpenChatVia, isMobile]) => {
          // A11Y.1 1.4.10: sized by the viewport as well as the device, so a
          // narrow desktop window gets the full-width panel too.
          applyViaPanelSize(
            viaPanelSize({
              open: isOpenChatVia,
              isMobile,
              isNarrow: isNarrowViewport()
            })
          );
          if (isOpenChatVia && !isMobile) {
            this.viaNudgesService.setIsOpenViaOnce();
          }
        })
      ),
    { dispatch: false }
  );
}
